import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Camera } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Book', path: '/booking' },
    { name: 'Client Access', path: '/client-access' },
    { name: 'Live Event', path: '/live-event' },
  ];

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-md bg-black/80 border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="container-luxury">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <Camera className="w-8 h-8 text-[#C9A14A] group-hover:scale-110 transition-transform duration-300" />
            <span className="font-['Playfair_Display'] text-xl md:text-2xl font-light tracking-wide text-white">
              Level Photography
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'text-[#C9A14A]' : 'text-white'}`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            {user && (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className={`nav-link ${location.pathname.startsWith('/admin') ? 'text-[#C9A14A]' : 'text-white'}`}
                    data-testid="nav-link-admin"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/face-recognition"
                  className={`nav-link ${location.pathname === '/face-recognition' ? 'text-[#C9A14A]' : 'text-white'}`}
                  data-testid="nav-link-face-recognition"
                >
                  Face Search
                </Link>
              </>
            )}
          </div>

          {/* Desktop Auth & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-sm text-gray-400">
                  {user.name}
                </span>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-white hover:text-[#C9A14A] text-xs uppercase tracking-wider"
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="login-button">
                <Button variant="ghost" className="text-white hover:text-[#C9A14A] text-xs uppercase tracking-wider">
                  Login
                </Button>
              </Link>
            )}
            <Link to="/booking" data-testid="book-now-button">
              <Button className="btn-gold">
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:text-[#C9A14A] transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden absolute w-full bg-black/95 backdrop-blur-md border-b border-white/10 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        data-testid="mobile-menu"
      >
        <div className="container-luxury py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block py-2 nav-link ${location.pathname === link.path ? 'text-[#C9A14A]' : 'text-white'}`}
              data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
            >
              {link.name}
            </Link>
          ))}
          
          {user && (
            <>
              {isAdmin && (
                <Link to="/admin" className="block py-2 nav-link text-white" data-testid="mobile-nav-link-admin">
                  Admin
                </Link>
              )}
              <Link to="/face-recognition" className="block py-2 nav-link text-white" data-testid="mobile-nav-link-face-recognition">
                Face Search
              </Link>
            </>
          )}
          
          <div className="pt-4 border-t border-white/10 space-y-3">
            {user ? (
              <Button
                onClick={handleLogout}
                className="w-full btn-outline"
                data-testid="mobile-logout-button"
              >
                Logout
              </Button>
            ) : (
              <Link to="/login" className="block">
                <Button className="w-full btn-outline" data-testid="mobile-login-button">
                  Login
                </Button>
              </Link>
            )}
            <Link to="/booking" className="block">
              <Button className="w-full btn-gold" data-testid="mobile-book-now-button">
                Book Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
